import { Injectable } from '@angular/core';
import { Validators } from '@angular/forms';

import { of } from 'rxjs';

import {
  CamAbstractInput,
  IInputTemplateDynamic,
  InputDropdown,
  InputDynamic,
  InputPanel,
  InputTimePicker,
} from '@camelot/form-model';
import { dateTimeToTimeString } from '@camelot/utils';
import { extractEnum } from '@camelot/utils';

import {
  DayAvailability,
  ModifyDayAvailabilitiesPayloadInput,
  ModifyDayAvailabilityPayloadInput,
  dayOfTheWeek,
} from '../dto/day-availability';

export enum EAvailabilitesFormFields {
  dayOfWeek = 'dayOfWeek',
  startTime = 'startTime',
  endTime = 'endTime',
}

@Injectable({
  providedIn: 'root',
})
export class CamAvailabilitiesFormService {
  public getAvailabilitiesEditForm(dayAvailabilities?: DayAvailability[] | null, withLabel: boolean = true) {
    const daysOptions$ = of(
      extractEnum(dayOfTheWeek).map(item => ({
        id: item.value.toString(),
        name: `user.dayOfTheWeek.${item.name.toLowerCase()}`,
      }))
    );

    const template: IInputTemplateDynamic[] = [
      {
        type: 'InputPanel',
        options: {
          key: 'availability-panel',
          label: '',
          templateChildren: () => [
            new InputPanel({
              key: 'availability-panel',
              contentClass: 'row g-1',
              children: [
                new InputDropdown({
                  key: EAvailabilitesFormFields.dayOfWeek,
                  label: 'user.dayAvailability.form.dayOfWeek',
                  options: daysOptions$,
                  class: 'col-4',
                  validators: [Validators.required],
                }),
                new InputTimePicker({
                  key: EAvailabilitesFormFields.startTime,
                  label: 'user.dayAvailability.form.startTime',
                  class: 'col-4',
                }),
                new InputTimePicker({
                  key: EAvailabilitesFormFields.endTime,
                  label: 'user.dayAvailability.form.endTime',
                  class: 'col-4',
                }),
              ],
            }),
          ],
        },
      },
    ];

    const inputsGroup: { [key: string]: CamAbstractInput[] } = (dayAvailabilities ?? []).reduce<{
      [key: string]: CamAbstractInput[];
    }>((acc, dayAvailability) => {
      const id = 'g-' + dayAvailability.id;

      acc[id] = [
        new InputPanel({
          key: 'availability-panel',
          contentClass: 'row g-1',
          children: [
            new InputDropdown({
              key: EAvailabilitesFormFields.dayOfWeek,
              label: 'user.dayAvailability.form.dayOfWeek',
              options: daysOptions$,
              class: 'col-4',
              validators: [Validators.required],
              value: dayAvailability.dayOfWeek?.toString(),
            }),
            new InputTimePicker({
              key: EAvailabilitesFormFields.startTime,
              label: 'user.dayAvailability.form.startTime',
              class: 'col-4',
            }),
            new InputTimePicker({
              key: EAvailabilitesFormFields.endTime,
              label: 'user.dayAvailability.form.endTime',
              class: 'col-4',
            }),
          ],
        }),
      ];

      return acc;
    }, {});

    return [
      new InputPanel({
        key: 'panel',
        label: withLabel ? 'user.dayAvailability.form.title' : '',
        contentClass: 'highlight-title g-space-md',
        children: [
          new InputDynamic({
            key: 'availabilities',
            template: template,
            inputsGroup: inputsGroup,
            enableDelete: true,
          }),
        ],
      }),
    ];
  }

  public formatUpdateAvailabilityForm(
    data: {
      availabilities: {
        [id: string]: {
          dayOfWeek: string;
          startTime: string;
          endTime: string;
        };
      };
    },
    functionId: string,
    userId?: string
  ): ModifyDayAvailabilitiesPayloadInput {
    const availabilities: ModifyDayAvailabilityPayloadInput[] = Object.entries(data.availabilities).map(
      ([id, value]) => {
        const guid = id.startsWith('availabilities-g-') ? id.replace(/^availabilities-g-/, '') : null;

        return {
          id: guid,
          dayOfWeek: Number(value.dayOfWeek),
          startTime: dateTimeToTimeString(value.startTime),
          endTime: dateTimeToTimeString(value.endTime),
        };
      }
    );

    return {
      functionId,
      userId,
      dayAvailabilities: availabilities,
    };
  }
}
